import React from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Avatar,
  Divider,
  SimpleGrid,
  Icon,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Flex,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiMail,
  FiPhone,
  FiCalendar,
  FiBook,
  FiShield,
  FiMapPin,
  FiClock,
  FiUser,
  FiMessageSquare,
} from 'react-icons/fi';
import { MdLocationOn } from 'react-icons/md';

// ── helpers ────────────────────────────────────────────────────────────────
const fmt = (d) =>
  d
    ? new Date(d).toLocaleDateString('fr-TN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—';

const fmtFull = (d) =>
  d
    ? new Date(d).toLocaleString('fr-TN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const ROLE_MAP = { admin: { label: 'مدير', color: 'red' }, instructor: { label: 'مدرس', color: 'purple' }, student: { label: 'طالب', color: 'blue' } };

// ── sub-components ─────────────────────────────────────────────────────────
const InfoRow = ({ icon, label, value, valueColor = 'gray.800' }) => (
  <HStack spacing={3} align="flex-start" py={1}>
    <Icon as={icon} color="blue.400" boxSize={4} mt="2px" flexShrink={0} />
    <Box flex={1}>
      <Text fontSize="xs" color="gray.400" fontWeight="600" letterSpacing="wide">
        {label}
      </Text>
      <Text fontSize="sm" color={valueColor} fontWeight="500" fontFamily="Cairo, sans-serif">
        {value || '—'}
      </Text>
    </Box>
  </HStack>
);

const SectionTitle = ({ children }) => (
  <Text
    fontSize="xs"
    fontWeight="700"
    color="gray.400"
    letterSpacing="wider"
    textTransform="uppercase"
    mt={4}
    mb={2}
  >
    {children}
  </Text>
);

// ── main component ─────────────────────────────────────────────────────────
const UserDetailsDrawer = ({ user, isOpen, onClose }) => {
  if (!user) return null;

  const role = ROLE_MAP[user.role] || ROLE_MAP.student;
  const isActive =
    user.lastlogin &&
    new Date(user.lastlogin) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

  const payInfo = user.paymentInfo;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <DrawerContent borderRadius="2xl 0 0 2xl" overflow="hidden">
        {/* ── Gradient header ─────────────────────────────────────────── */}
        <Box bgGradient="linear(135deg, #1a365d 0%, #2b6cb0 60%, #3182ce 100%)" pb={6}>
          <DrawerCloseButton color="white" top={3} right={3} />
          <DrawerHeader pb={0} pt={5}>
            <VStack spacing={3} align="center">
              <Avatar
                size="xl"
                name={user.username}
                bg="yellow.400"
                color="gray.900"
                fontWeight="bold"
                fontSize="2xl"
                boxShadow="0 0 0 4px rgba(255,255,255,0.25)"
              />
              <VStack spacing={1}>
                <Text color="white" fontWeight="bold" fontSize="xl" fontFamily="Cairo, sans-serif">
                  {user.username}
                </Text>
                <Text color="blue.100" fontSize="sm">
                  {user.email}
                </Text>
                <HStack spacing={2} mt={1}>
                  <Badge
                    colorScheme={role.color}
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontFamily="Cairo, sans-serif"
                    fontWeight="700"
                  >
                    {role.label}
                  </Badge>
                  <Badge
                    colorScheme={isActive ? 'green' : 'gray'}
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontFamily="Cairo, sans-serif"
                  >
                    {isActive ? '🟢 نشط' : '⚪ غير نشط'}
                  </Badge>
                </HStack>
              </VStack>
            </VStack>
          </DrawerHeader>
        </Box>

        {/* ── Body ────────────────────────────────────────────────────── */}
        <DrawerBody bg="gray.50" px={5} py={4} overflowY="auto">
          <VStack align="stretch" spacing={0}>

            {/* Contact */}
            <SectionTitle>معلومات الاتصال</SectionTitle>
            <Box bg="white" borderRadius="xl" p={4} shadow="sm">
              <VStack align="stretch" spacing={2} divider={<Divider />}>
                <InfoRow icon={FiMail} label="البريد الإلكتروني" value={user.email} />
                <InfoRow icon={FiPhone} label="رقم الهاتف" value={user.phoneNumber || '—'} />
                <InfoRow icon={FiPhone} label="هاتف ولي الأمر" value={user.parentPhoneNumber || '—'} />
                <InfoRow icon={FiMessageSquare} label="واتساب (الطالب)" value={user.whatsappNumberUser || '—'} />
                <InfoRow icon={FiMessageSquare} label="واتساب (الأسرة)" value={user.whatsappNumberParent || '—'} />
              </VStack>
            </Box>

            {/* Location (GIC) */}
            {(user.wilaya || user.moughataa) && (
              <>
                <SectionTitle>الموقع الجغرافي</SectionTitle>
                <Box bg="white" borderRadius="xl" p={4} shadow="sm">
                  <VStack align="stretch" spacing={2} divider={<Divider />}>
                    <InfoRow icon={MdLocationOn} label="الولاية" value={user.wilaya} />
                    <InfoRow icon={FiMapPin} label="المعتمدية" value={user.moughataa} />
                  </VStack>
                </Box>
              </>
            )}

            {/* Activity */}
            <SectionTitle>النشاط</SectionTitle>
            <Box bg="white" borderRadius="xl" p={4} shadow="sm">
              <VStack align="stretch" spacing={2} divider={<Divider />}>
                <InfoRow icon={FiCalendar} label="تاريخ الانضمام" value={fmt(user.inscriptionDate || user.createdAt)} />
                <InfoRow icon={FiClock} label="آخر تسجيل دخول" value={fmtFull(user.lastlogin)} valueColor={isActive ? 'green.600' : 'gray.800'} />
              </VStack>
            </Box>

            {/* Matières */}
            {user.matiers && user.matiers.length > 0 && (
              <>
                <SectionTitle>المواد الدراسية</SectionTitle>
                <Box bg="white" borderRadius="xl" p={4} shadow="sm">
                  <Wrap spacing={2}>
                    {user.matiers.map((m, i) => (
                      <WrapItem key={i}>
                        <Tag colorScheme="blue" borderRadius="full" size="md">
                          <Icon as={FiBook} mr={1} boxSize={3} />
                          <TagLabel fontFamily="Cairo, sans-serif">{m}</TagLabel>
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              </>
            )}

            {/* Payment */}
            {payInfo && (
              <>
                <SectionTitle>معلومات الدفع</SectionTitle>
                <SimpleGrid columns={2} spacing={3}>
                  <Box bg="white" borderRadius="xl" p={4} shadow="sm" textAlign="center">
                    <Text fontSize="xs" color="gray.400" mb={1}>المبلغ الإجمالي</Text>
                    <Text fontSize="xl" fontWeight="800" color="gray.800" fontFamily="Inter">
                      {payInfo.totalAmount?.toLocaleString()}
                    </Text>
                    <Text fontSize="xs" color="gray.400">TND</Text>
                  </Box>
                  <Box bg="white" borderRadius="xl" p={4} shadow="sm" textAlign="center">
                    <Text fontSize="xs" color="gray.400" mb={1}>المدفوع</Text>
                    <Text fontSize="xl" fontWeight="800" color="green.600" fontFamily="Inter">
                      {payInfo.paidAmount?.toLocaleString()}
                    </Text>
                    <Text fontSize="xs" color="gray.400">TND</Text>
                  </Box>
                  <Box bg="white" borderRadius="xl" p={4} shadow="sm" textAlign="center">
                    <Text fontSize="xs" color="gray.400" mb={1}>المبلغ المتبقي</Text>
                    <Text fontSize="xl" fontWeight="800" color={payInfo.remainingAmount > 0 ? 'orange.500' : 'green.600'} fontFamily="Inter">
                      {payInfo.remainingAmount?.toLocaleString()}
                    </Text>
                    <Text fontSize="xs" color="gray.400">TND</Text>
                  </Box>
                  <Box bg="white" borderRadius="xl" p={4} shadow="sm" textAlign="center">
                    <Text fontSize="xs" color="gray.400" mb={1}>الأقساط المتبقية</Text>
                    <Text fontSize="xl" fontWeight="800" color={payInfo.remaining > 0 ? 'orange.500' : 'green.600'} fontFamily="Inter">
                      {payInfo.remaining} / {payInfo.total}
                    </Text>
                    <Text fontSize="xs" color="gray.400">قسط</Text>
                  </Box>
                </SimpleGrid>
                {payInfo.deadlineDate && (
                  <Box bg="white" borderRadius="xl" p={4} shadow="sm" mt={3}>
                    <InfoRow
                      icon={FiCalendar}
                      label="تاريخ انتهاء صلاحية الدفع"
                      value={fmt(payInfo.deadlineDate)}
                      valueColor={new Date(payInfo.deadlineDate) < new Date() && payInfo.remainingAmount > 0 ? 'red.500' : 'gray.800'}
                    />
                  </Box>
                )}
              </>
            )}

            {/* System info */}
            <SectionTitle>معلومات النظام</SectionTitle>
            <Box bg="white" borderRadius="xl" p={4} shadow="sm" mb={4}>
              <VStack align="stretch" spacing={2} divider={<Divider />}>
                <InfoRow icon={FiShield} label="الدور" value={role.label} />
                <InfoRow icon={FiUser} label="المعرّف" value={user._id?.toString()} valueColor="gray.400" />
              </VStack>
            </Box>

          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default UserDetailsDrawer;
